/**
 * Meta Pixel service for Facebook tracking
 * Integrates with existing analytics infrastructure
 */

import ReactPixel from 'react-facebook-pixel';

interface MetaPixelConfig {
  pixelId: string;
  enabled: boolean;
}

class MetaPixelService {
  private pixelId: string | null = null;
  private isInitialized = false;
  private isEnabled = false;

  initialize(pixelId: string, enabled: boolean = true): void {
    if (typeof window === 'undefined') return;
    
    this.pixelId = pixelId;
    this.isEnabled = enabled;
    
    if (enabled && pixelId) {
      try {
        ReactPixel.init(pixelId);
        ReactPixel.pageView();
        this.isInitialized = true;
        
        if (import.meta.env.DEV) {
          console.log('[Meta Pixel] Initialized with ID:', pixelId);
        }
      } catch (error) {
        console.error('[Meta Pixel Error]', error);
      }
    }
  }

  trackEvent(eventName: string, parameters?: Record<string, any>): void {
    if (!this.isEnabled || !this.isInitialized) return;
    
    try {
      ReactPixel.track(eventName, parameters);
      
      if (import.meta.env.DEV) {
        console.log('[Meta Pixel]', eventName, parameters);
      }
    } catch (error) {
      console.error('[Meta Pixel Event Error]', error);
    }
  }

  trackPageView(): void {
    if (!this.isEnabled || !this.isInitialized) return;
    
    try {
      ReactPixel.pageView();
    } catch (error) {
      console.error('[Meta Pixel PageView Error]', error);
    }
  }

  trackPurchase(value: number, currency: string = 'USD', contentIds?: string[]): void {
    this.trackEvent('Purchase', {
      value: value,
      currency: currency,
      content_ids: contentIds,
      content_type: 'product'
    });
  }

  trackAddToCart(value: number, currency: string = 'USD', contentIds?: string[]): void {
    this.trackEvent('AddToCart', {
      value: value,
      currency: currency,
      content_ids: contentIds,
      content_type: 'product'
    });
  }

  trackInitiateCheckout(value: number, currency: string = 'USD', contentIds?: string[]): void {
    this.trackEvent('InitiateCheckout', {
      value: value,
      currency: currency,
      content_ids: contentIds,
      content_type: 'product'
    });
  }

  trackCompleteRegistration(): void {
    this.trackEvent('CompleteRegistration');
  }

  trackLead(): void {
    this.trackEvent('Lead');
  }

  trackContact(): void {
    this.trackEvent('Contact');
  }

  trackDonation(value: number, currency: string = 'USD', campaignId?: string): void {
    this.trackEvent('Donate', {
      value: value,
      currency: currency,
      content_ids: campaignId ? [campaignId] : undefined,
      content_type: 'product'
    });
  }

  // Method to update user data
  updateUserData(userData: Record<string, any>): void {
    if (!this.isEnabled || !this.isInitialized) return;
    
    try {
      ReactPixel.track('CustomizeProduct', userData);
    } catch (error) {
      console.error('[Meta Pixel User Data Error]', error);
    }
  }

  // Method to check if Meta Pixel is available
  isAvailable(): boolean {
    return this.isEnabled && this.isInitialized;
  }

  // Method to get current configuration
  getConfig(): { pixelId: string | null; enabled: boolean; initialized: boolean } {
    return {
      pixelId: this.pixelId,
      enabled: this.isEnabled,
      initialized: this.isInitialized
    };
  }
}

export const metaPixelService = new MetaPixelService(); 